/**
 * Plan multi-comparison data model + mock provider (TPX-1903).
 *
 * Single source of truth for the shape the whole feature renders against. The backend for >1
 * comparison is a separate ticket, so per-comparison VALUES are mocked here; swapping to the real
 * API is an adapter-only change (see `attachMockComparisonValues` / `getComparisonValue`).
 *
 * KEY: every comparison is identified across all surfaces by `planComparisonName` (backend-verified
 * unique key). The header response `id` is the plan being compared (identical on every entry) and is
 * NOT a per-comparison identifier — do not key by it.
 *
 * Canonical comparison object (used by dialog, header, highlights, roll-ups, grid, edit value):
 *   {
 *     key,                // string — planComparisonName; the stable per-comparison key
 *     name,               // string — display label (same as key today)
 *     sortOrder,          // number — comparisonSortOrder (1-based)
 *     comparisonTarget,   // "actuals" | "specific" | "snapshot" (UI radio)
 *     dataType,           // "Actuals" | "Plan" | "Snapshot" (persisted)
 *     compareToPropertyId,// number | null
 *     year,               // number | null
 *     planType,           // "Budget" | "What if" | "Forecast" | null
 *     compareToPlanId,    // number | null
 *     versionName,        // string | null
 *     snapshotDate,       // "YYYY-MM-DD" | null
 *   }
 */
import {
  MAX_COMPARISONS,
  COMPARISON_DEFAULT_NAME_PREFIX,
  COMPARISON_TARGET,
  COMPARISON_DATA_TYPE,
} from "../../../constants/keywords";

/** Derive the UI radio target from a persisted/response `dataType`. */
export function targetFromDataType(dataType) {
  if (dataType === COMPARISON_DATA_TYPE.ACTUALS) return COMPARISON_TARGET.ACTUALS;
  if (dataType === COMPARISON_DATA_TYPE.SNAPSHOT) return COMPARISON_TARGET.SNAPSHOT;
  if (dataType === COMPARISON_DATA_TYPE.TRAILING_12) return COMPARISON_TARGET.TRAILING_12;
  return COMPARISON_TARGET.SPECIFIC;
}

/** Derive the persisted `dataType` from the UI radio target. */
export function dataTypeFromTarget(comparisonTarget) {
  if (comparisonTarget === COMPARISON_TARGET.ACTUALS) return COMPARISON_DATA_TYPE.ACTUALS;
  if (comparisonTarget === COMPARISON_TARGET.SNAPSHOT) return COMPARISON_DATA_TYPE.SNAPSHOT;
  if (comparisonTarget === COMPARISON_TARGET.TRAILING_12) return COMPARISON_DATA_TYPE.TRAILING_12;
  return COMPARISON_DATA_TYPE.PLAN;
}

/** Default display name for the row at zero-based `index`: "Comparison 1", "Comparison 2", ... */
export function defaultComparisonName(index) {
  return `${COMPARISON_DEFAULT_NAME_PREFIX} ${index + 1}`;
}

/**
 * Resolve a comparison's display name from a backend entry. ALWAYS prefers the backend-provided
 * `planComparisonName` (tolerating `comparisonName` / `name` aliases). Only when it is truly absent
 * (e.g. an earlier single-comparison plan that never had a name) does it fall back to a non-static
 * default keyed off the comparison's own sort order — "Comparison 1", "Comparison 2", …
 */
export function resolveComparisonName(entry, index) {
  const raw = entry?.planComparisonName ?? entry?.comparisonName ?? entry?.name;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  const order = entry?.comparisonSortOrder;
  const oneBased = Number.isFinite(Number(order)) && Number(order) > 0 ? Number(order) : index + 1;
  return defaultComparisonName(oneBased - 1);
}

/**
 * Map one header/config response entry
 * ({ id, planType, dataType, versionName, propertyId, fiscalYear, snapShotDate, planComparisonName })
 * to the canonical comparison object. Falls back to the current plan's property/year/planType when a
 * field is null (mirrors the legacy dialog behavior).
 */
export function adaptComparisonEntry(entry, index, planDetails) {
  const name = resolveComparisonName(entry, index);
  const dataType = entry?.dataType || COMPARISON_DATA_TYPE.PLAN;
  return {
    key: name,
    name,
    sortOrder: entry?.comparisonSortOrder ?? index + 1,
    comparisonTarget: targetFromDataType(dataType),
    dataType,
    compareToPropertyId:
      entry?.compareToPropertyId ?? entry?.propertyId ?? planDetails?.propertyIds?.[0] ?? null,
    year: entry?.year ?? entry?.fiscalYear ?? planDetails?.fiscalYear ?? null,
    planType: entry?.planType ?? null,
    compareToPlanId: entry?.compareToPlanId ?? entry?.id ?? null,
    versionName: entry?.versionName ?? null,
    snapshotDate: entry?.snapShotDate ?? entry?.snapshotDate ?? null,
  };
}

/**
 * Back-compat: adapt the legacy single-record `plan-comparison/{id}` response to a 1-element
 * canonical array. Returns [] when no comparison is configured.
 */
export function adaptLegacyComparison(legacyPayload, planDetails) {
  if (!legacyPayload) return [];
  const hasAny =
    legacyPayload.compareToPropertyId != null ||
    legacyPayload.propertyId != null ||
    (legacyPayload.dataType === COMPARISON_DATA_TYPE.SNAPSHOT &&
      (legacyPayload.snapShotDate || legacyPayload.snapshotDate));
  if (!hasAny) return [];
  // Use the payload's own name when present; adaptComparisonEntry falls back to "Comparison 1" only
  // when the legacy record genuinely has no name. (Previously this forced "Comparison 1" statically,
  // which clobbered real backend names — the bug reported in review.)
  return [adaptComparisonEntry(legacyPayload, 0, planDetails)];
}

/**
 * Adapt the multi-comparison header response `comparison[]` to the canonical array, sorted by
 * `comparisonSortOrder`. When the array is empty/absent, falls back to the legacy single-record
 * adapter so single-comparison plans keep working with no migration.
 */
export function adaptComparisons(response, planDetails) {
  const arr = Array.isArray(response?.comparison)
    ? response.comparison
    : Array.isArray(response)
      ? response
      : null;
  if (!arr || arr.length === 0) return adaptLegacyComparison(response, planDetails);
  return arr
    .map((entry, i) => adaptComparisonEntry(entry, i, planDetails))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Build the save request `compareTo[]` (verified contract) from the canonical comparisons + the
 * global placement/display choices. Field names are backend-verified — do not rename.
 */
export function buildComparisonSaveRequest({ planId, comparisons, placement, isPercentageView }) {
  return {
    planId: parseInt(planId, 10),
    showInHighlights: !!placement?.showInHighlights,
    showInRollups: !!placement?.showInRollups,
    showInPlanTable: !!placement?.showInPlanTable,
    isPercentageView: !!isPercentageView,
    compareTo: (comparisons || []).map((c, i) => {
      const isActuals = c.comparisonTarget === COMPARISON_TARGET.ACTUALS;
      const isSpecific = c.comparisonTarget === COMPARISON_TARGET.SPECIFIC;
      const isSnapshot = c.comparisonTarget === COMPARISON_TARGET.SNAPSHOT;
      const isTrailing12 = c.comparisonTarget === COMPARISON_TARGET.TRAILING_12;
      return {
        // TPX-1902: Trailing 12 month sends only the plan's own property (window is derived server-side);
        // year / planType / compareToPlanId / versionName / snapshotDate are all null for this target.
        compareToPropertyId:
          isActuals || isSpecific || isTrailing12 ? c.compareToPropertyId ?? null : null,
        year: isActuals || isSpecific ? c.year ?? null : null,
        dataType: dataTypeFromTarget(c.comparisonTarget),
        planType: isSpecific || isSnapshot ? c.planType ?? null : null,
        compareToPlanId: isSpecific ? c.compareToPlanId ?? null : null,
        versionName: isSpecific ? c.versionName ?? null : null,
        snapshotDate: isSnapshot ? c.snapshotDate ?? null : null,
        planComparisonName: c.name || defaultComparisonName(i),
        comparisonSortOrder: i + 1,
      };
    }),
  };
}

/* -------------------------------------------------------------------------- */
/* Per-comparison value readers (backend returns real numbered fields)         */
/* -------------------------------------------------------------------------- */

/* Mock generator removed (TPX-1903 fix): the backend now returns real numbered per-comparison values
 * (comparisonValue2/3, change2/3, amountVar2/3, yearComp2/3, …). All surfaces read those directly. */

/**
 * Read a per-comparison cell from a row/period, tolerant of both the target keyed shape
 * (`data[YYYYMM].comparisons[key]`) and the legacy single-comparison scalar
 * (`data[YYYYMM].comparisonValue` / `.variance`). Returns { comparisonValue, variance } | null.
 */
export function getComparisonValue(periodData, comparisonKey) {
  if (!periodData) return null;
  const keyed = periodData.comparisons?.[comparisonKey];
  if (keyed && (keyed.comparisonValue != null || keyed.variance != null)) {
    return { comparisonValue: keyed.comparisonValue ?? null, variance: keyed.variance ?? null };
  }
  if (periodData.comparisonValue != null || periodData.variance != null) {
    return { comparisonValue: periodData.comparisonValue ?? null, variance: periodData.variance ?? null };
  }
  return null;
}

/** True once the real (keyed) comparison values are present on a period cell. */
export function hasKeyedComparisonValues(periodData) {
  return !!periodData?.comparisons && Object.keys(periodData.comparisons).length > 0;
}

export { MAX_COMPARISONS };
